import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../../utils/api';
import { User, Mail, Phone, Shield, Edit } from 'lucide-react';
import PageHeading from '../../components/PageHeading';

function AdminDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: admin, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', id],
    queryFn: async ({ signal }) => {
      const response = await api.get(`/admins/${id}`, { signal });
      return response.data;
    },
  });

  if (isLoading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-700 font-medium">Could not load admin</p>
        <p className="text-sm text-gray-600 mt-2">{error?.message || 'Request failed'}</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!admin) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 mb-4">Admin not found.</p>
        <button
          type="button"
          onClick={() => navigate('/admins')}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Back to Admins
        </button>
      </div>
    );
  }

  return (
    <div>
      <PageHeading
        title={admin.name}
        backTo="/admins"
        backLabel="Back to Admins"
        actions={
          <Link
            to={`/admins/${admin.id}/edit`}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Edit className="w-5 h-5 mr-2" />
            Edit
          </Link>
        }
      />

      <div className="bg-card rounded-lg shadow p-6 space-y-4">
        <div className="flex items-center text-gray-700">
          <User className="w-5 h-5 mr-3 text-gray-400" />
          <span className="font-medium mr-2">Username:</span>
          {admin.username}
        </div>
        <div className="flex items-center text-gray-700">
          <Mail className="w-5 h-5 mr-3 text-gray-400" />
          <span className="font-medium mr-2">Email:</span>
          {admin.email || '—'}
        </div>
        <div className="flex items-center text-gray-700">
          <Phone className="w-5 h-5 mr-3 text-gray-400" />
          <span className="font-medium mr-2">Phone:</span>
          {admin.phone || '—'}
        </div>
        <div className="flex items-center text-gray-700">
          <Shield className="w-5 h-5 mr-3 text-gray-400" />
          <span className="font-medium mr-2">Role:</span>
          {admin.role}
        </div>
        <div className="pt-2">
          <span
            className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
              admin.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
            }`}
          >
            {admin.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>
    </div>
  );
}

export default AdminDetail;
